'use strict'

function section (heading, paragraphs, items) {

  const block = { heading, paragraphs: paragraphs.filter(Boolean) }

  if (items && items.length) block.items = items

  return block
}

function naming (noun) {

  return section('Title and unique title', [
    `Title is the name people read. Change it whenever the label should change. Nothing else in the app looks up this ${noun} by its title.`,
    `Unique title is the stored identifier. It has to start with a letter or an underscore. After that it may contain letters, digits, and underscores. Spaces, hyphens, and punctuation are rejected.`,
    `Typing a title fills the unique title until you edit the unique title yourself. After that, title changes leave the identifier alone.`,
    `Pages, views, menus, automations, and schedules store this unique title when they point at the ${noun}. Renaming the identifier does not rewrite those references, so they keep the old name and stop finding this ${noun}.`
  ])
}

function saving (okLabel, what) {

  return section('Saving this dialog', [
    `${okLabel} writes ${what}. Cancel, the close button, and Escape throw away the edits in this dialog and leave the last saved copy in place.`,
    `Help stays open beside the dialog so you can read a setting and change it without losing your place. Close help with its own close button, or press Escape. Escape closes the help and leaves this dialog open. Closing the dialog closes the help as well.`
  ])
}

function aiTab (subject) {

  return section('AI tab', [
    `Describe the change in plain language. The button at the bottom of the dialog becomes "update with AI" while this tab is selected, and that button is the one that sends the description.`,
    `The AI updates this saved ${subject} through the page API. It does not apply the other tabs first. If you also changed General, Layout, or Filters by hand, save those with OK on one of those tabs before you ask the AI to change the ${subject}, or those unsaved edits are not part of what the AI sees.`,
    `A token line on the tab shows input plus output tokens for this billing period, out of the account allotment. The request can take up to a minute. The button stays disabled while it runs. An error stays on the tab so you can edit the description and try again.`
  ])
}

function realtime () {

  return section('Realtime', [
    `When realtime is on, the live view refreshes itself when a record on its spreadsheet is created, updated, or deleted. Visitors do not have to reload the page to see another person's save.`,
    `Leave it off when the view should stay on the snapshot that loaded with the page. Filters and sorts still apply either way. Realtime does not bypass a prefilter.`
  ])
}

function pageRecord (behavior) {

  return section('Page record', [
    `This block appears only when the page is opened from a link that carries a record. The link remembers which record was open, and this view can use that record.`,
    behavior,
    `The relationship it uses is named under the checkbox. Turn the checkbox off when this view should show the whole spreadsheet even if the visitor arrived from a specific record.`
  ])
}

function searchAndFilters (viewNoun) {

  return section('Search', [
    `Show a search box lets a visitor type text and narrow the records in this ${viewNoun}. Password columns are never searched.`,
    `Search the fields on this view looks through the columns you placed on the ${viewNoun}, including columns that come from a related spreadsheet.`,
    `Search every field on the spreadsheet also matches columns that are not shown. A visitor can learn that a hidden value exists even though that column stays off the ${viewNoun}. Use that scope only when those hidden columns are safe to probe.`
  ])
}

function userFilters () {

  return section('Filters visitors can change', [
    `Allow users to filter records shows the filter bar on the live view. Visitors pick a field, an operator, and a value, and they choose whether every condition must match or any condition may match.`,
    `Restrict filter fields to fields on this view limits that picker to the columns you added here. Leave it unchecked and the picker also lists other columns on the spreadsheet and columns reached through a relationship.`,
    `Default filters are the conditions the view opens with. They are visible, and a visitor can change or clear them. They are a starting point, not a lock.`,
    `Each default condition is a field, an operator, and a value. Match mode All means every condition must be true. Match mode Any means one true condition is enough.`
  ])
}

function filterButtons () {

  return section('Filter buttons', [
    'Filter buttons are optional shortcuts on the live view. Each button has a name and a set of filter rules. Visitors see the name. They do not see the field, the operator, or the value.',
    'A button looks selected while its rules are applied. Click it again to turn that shortcut off.',
    'Allow more than one filter button at a time keeps every selected button on. A record has to match every selected button. Leave it off when choosing a button should turn the previous one off and apply only the new one.',
    'Add filter button appends a button. Type the name visitors will read, then add the rules that button applies. The rules use the same fields and operators as a prefilter. Match all rules or any rule inside that one button.',
    'A button with no rules does not narrow the records. Remove a button with the x on its card. The rules stay on the view. The live page shows the button name only.'
  ])
}

function prefilters () {

  return section('Prefilters', [
    `A prefilter is always applied and is not shown on the live view. Visitors cannot turn it off, and it is not listed in the filter bar.`,
    `Use a prefilter for a rule the view must obey, such as status equals Open, or a relationship that should never show records from another account's row.`,
    `Prefilters combine with search, with the visitor's own filters, and with the page-record relationship. A record has to pass all of those layers.`,
    `Match mode All requires every prefilter. Match mode Any keeps a record when at least one prefilter matches. An empty prefilter list does not restrict the set.`
  ])
}

function sorting (subject) {

  return section('Sort order', [
    `${subject} The first sort is the highest precedence. Later sorts break ties.`,
    `Each row is a column and a direction, ascending or descending. Ascending is smallest to largest for numbers, A to Z for text, and earliest to latest for dates.`,
    `System columns such as created and updated time can be sorted when they appear in the column list. A column that is not on the view can still be a sort if the spreadsheet has it.`
  ])
}

function columnTypes () {

  return section('Column types', [
    `Number stores a numeric value. Text stores a string. Password stores a secret and is hashed when the record is saved. Date time stores a date and time.`,
    `Relationship points at records on another spreadsheet. Image and file store an uploaded file. Rich text stores formatted content. Address stores a location string that a map can look up.`,
    `Formula stores an expression. The value is calculated. It is not typed in by hand on a record.`,
    `Multiselect stores one choice or many choices from a list you define on the column. The form, table, or cards view decides whether those choices appear as checkboxes, radio buttons, a dropdown, or a multiple-select list.`
  ], [
    `Number, text, password, date time, relationship, image, file, formula, rich text, multiselect, and address are the types this dialog can create.`
  ])
}

function relationshipTypes () {

  return section('Relationship', [
    `Related spreadsheet is the other sheet this column points at. The column does not copy that sheet's fields. It stores links to records.`,
    `One to one means one record here links to one record there, and that record there is not shared with another record here.`,
    `One to many means one record here links to many records there.`,
    `Many to one means many records here can link to the same one record there.`,
    `Many to many means records on both sides can link to many records on the other side.`,
    `The wording in the dropdown names both spreadsheets so you can read the direction before you save. The link is stored in a bridge table for this column. Deleting the column removes that link storage.`
  ])
}

function imageResize () {

  return section('Image auto-resize', [
    `Resize on upload scales the file when it is saved, before it is stored. Later views show the stored file. They do not resize it again.`,
    `Keep proportions scales the image so both sides stay inside the width and height you enter. The shape stays the same. The boxes are maximums, not an exact size.`,
    `With keep proportions off, the image is stretched to that exact width and height.`,
    `Width and height are pixels. Leave resize off to store the upload at its original size.`
  ])
}

function multiselectColumn () {

  return section('Multiselect choices', [
    `Selection mode Single select saves one value. Multiple select saves every chosen value. This is stored on the column. A form can still choose a different control, but it cannot save many values into a single-select column.`,
    `Choices are the selectable values. Each row is one choice. The text you type is both the stored value and the label the visitor sees. Remove a choice with x. Add choice appends a blank row.`,
    `Removing a choice here does not rewrite records that already stored that value. Those records keep the old text until someone edits them.`
  ])
}

function valueFormatting () {

  return section('Value formatting', [
    `Value formatting changes how the stored value is displayed. It does not change the value saved on the record.`,
    `Number formatting sets decimal places, rounding, a currency symbol, thousands and decimal separators, and an option to display the number as a percentage. A preview line shows an example of the current settings. Decimal places accepts 0 through 10. Leave it blank for automatic places. Rounding can round to the nearest value, down, or up.`,
    `Text formatting sets a maximum display length, a case transform, find-and-replace text, and three cleanups: strip HTML, strip characters that are not letters or digits, and trim whitespace. It can also show the text in bold, italic, or strikethrough, and choose a font and font size. The stored text stays as it was.`,
    `Clear formatting removes the display rules. The active badge on the heading means a format is currently saved. The section can be collapsed. Collapsing it does not clear the format.`
  ])
}

function formulaColumn () {

  return section('Formula', [
    `A formula column shows the result of an expression. The editor starts with = and suggests functions and fields as you type.`,
    `A field on this spreadsheet is written in braces, for example {price}. A field reached through relationships can be written as a path. The suggestion list labels functions, fields, and related fields so you can tell them apart.`,
    `The formula is calculated when records are read and when a recalculate task runs. Visitors do not type into a formula cell.`
  ])
}

function columnWriteRules () {

  return section('Required, description, and default', [
    `Description is the help text for this column. A form can replace the label, the placeholder, and the description for that screen. When the form leaves the description blank, this text is shown.`,
    `Required rejects a save that leaves this value empty. That check runs on a form, an inline cell, the designer grid, the API, and a workflow. Import can leave the value empty.`,
    `Default value fills an empty value when a record is created. It does not replace a value someone typed, and it does not run again when a record is edited. A form rule can still set a value from other answers on that form.`
  ])
}

function uniqueConstraint () {

  return section('Unique values', [
    `Enforce unique values rejects a save when another record on this spreadsheet already has the same value in this column. Empty values are not treated as duplicates of each other.`,
    `The checkbox is offered for column types that can be compared as a single value. Turn it on for an email, a code, or another identifier that must not repeat.`
  ])
}

function deleteConfirm (noun) {

  return section('What delete does', [
    `Delete removes this ${noun}. The dialog asks you to confirm because the removal is not undone by Cancel after you confirm.`,
    `Type the title exactly when the dialog asks for it. The button stays available, but the delete is rejected until the title matches. That check is there so a mis-click on a dangerous button does not remove the ${noun}.`
  ])
}

module.exports = {
  section,
  naming,
  saving,
  aiTab,
  realtime,
  pageRecord,
  searchAndFilters,
  userFilters,
  filterButtons,
  prefilters,
  sorting,
  columnTypes,
  relationshipTypes,
  imageResize,
  multiselectColumn,
  valueFormatting,
  formulaColumn,
  columnWriteRules,
  uniqueConstraint,
  deleteConfirm
}
